import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/6791b343-2112-41a2-8ad4-0237b5984757/images/09005f6ceb692665c653e3d6ce651d87daf97bdf3c2ebabb9710fc3f6579f810.png";

export default function BookingPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/booking-requests", form);
      setSubmitted(true);
      toast.success("Request received — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="booking-page">
      <header className="border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={28} />
            <div>
              <div className="font-display text-xl leading-none">TRAINA</div>
              <div className="tracker text-zinc-500 mt-1 hidden sm:block">Run your training business with confidence</div>
            </div>
          </Link>
          <Link to="/login" className="tracker hover:text-white">Already a client? Sign in</Link>
        </div>
      </header>

      <div className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0A0A0A]/60" />
        <div className="relative max-w-[1200px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="tracker mb-4 text-brand">[ Request a session ]</div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[0.9] mb-6">
              Train with<br /><span className="text-brand">a real coach.</span>
            </h1>
            <p className="text-zinc-400 mb-8 leading-relaxed">
              The complete software for coaches and clients.
            </p>
            <ul className="space-y-3 text-zinc-300">
              {["1-on-1 personalized programming", "Free 15-min strategy intro", "Cancel or reschedule anytime"].map((t) => (
                <li key={t} className="flex items-center gap-3"><CheckCircle size={20} weight="duotone" className="text-brand" /> {t}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {submitted ? (
              <div className="card-flat p-10 text-center" data-testid="booking-success">
                <CheckCircle size={48} weight="duotone" className="text-brand mx-auto mb-4" />
                <h2 className="font-display text-3xl mb-3">Request received.</h2>
                <p className="text-zinc-400 mb-6">We'll reach out via email and SMS to confirm your session.</p>
                <Link to="/" className="btn-ghost">Back home</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="card-flat p-8 space-y-5" data-testid="booking-form">
                <div>
                  <label className="tracker block mb-2">Full name</label>
                  <input required value={form.name} onChange={set("name")} className="w-full px-4 py-3 border" data-testid="booking-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="tracker block mb-2">Email</label>
                    <input required type="email" value={form.email} onChange={set("email")} className="w-full px-4 py-3 border" data-testid="booking-email" />
                  </div>
                  <div>
                    <label className="tracker block mb-2">Phone</label>
                    <input value={form.phone} onChange={set("phone")} placeholder="+1 555 555 0123" className="w-full px-4 py-3 border" data-testid="booking-phone" />
                  </div>
                </div>
                <div>
                  <label className="tracker block mb-2">Preferred date / time</label>
                  <input required type="datetime-local" value={form.preferred_date} onChange={set("preferred_date")} className="w-full px-4 py-3 border" data-testid="booking-date" />
                </div>
                <div>
                  <label className="tracker block mb-2">Goals / notes</label>
                  <textarea rows={4} value={form.notes} onChange={set("notes")} className="w-full px-4 py-3 border" placeholder="Strength, mobility, fat loss, sport-specific..." data-testid="booking-notes" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="booking-submit">
                  {loading ? "Sending…" : "Request session"} <ArrowRight size={16} weight="bold" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
